import React, { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'

interface DeploymentCountdownProps {
  endDate: string
  onComplete?: () => void
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const DeploymentCountdown: React.FC<DeploymentCountdownProps> = ({ endDate, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime()
      const target = new Date(endDate).getTime()
      const difference = target - now

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24))
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((difference % (1000 * 60)) / 1000)

        setTimeLeft({ days, hours, minutes, seconds })
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        setIsComplete(true)
        onComplete?.()
      }
    }

    // Calculate immediately
    calculateTimeLeft()

    // Update every second
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [endDate, onComplete])

  if (isComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 3,
          backgroundColor: '#27ae60', // Green color
          borderRadius: 2,
          color: 'white'
        }}
      >
        <CheckCircle sx={{ fontSize: 32 }} />
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600
          }}
        >
          System Update Complete!
        </Typography>
      </Box>
    )
  }

  const formatNumber = (num: number) => num.toString().padStart(2, '0')

  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Countdown Timer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          mb: 3
        }}
      >
        {/* Hours */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 70
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffffff',
              borderRadius: 1,
              minWidth: 60,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #ecf0f1'
            }}
          >
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                lineHeight: 1
              }}
            >
              {formatNumber(timeLeft.hours)}
            </Typography>
          </Box>
          <Typography
            variant='caption'
            sx={{
              color: '#7f8c8d',
              fontWeight: 600,
              mt: 1,
              textTransform: 'uppercase',
              fontSize: '0.7rem'
            }}
          >
            Hours
          </Typography>
        </Box>

        {/* Separator Dot */}
        <Typography
          sx={{
            color: '#bdc3c7',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            mx: 1
          }}
        >
          :
        </Typography>

        {/* Minutes */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 70
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffffff',
              borderRadius: 1,
              minWidth: 60,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #ecf0f1'
            }}
          >
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                lineHeight: 1
              }}
            >
              {formatNumber(timeLeft.minutes)}
            </Typography>
          </Box>
          <Typography
            variant='caption'
            sx={{
              color: '#7f8c8d',
              fontWeight: 600,
              mt: 1,
              textTransform: 'uppercase',
              fontSize: '0.7rem'
            }}
          >
            Minutes
          </Typography>
        </Box>

        {/* Separator Dot */}
        <Typography
          sx={{
            color: '#bdc3c7',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            mx: 1
          }}
        >
          :
        </Typography>

        {/* Seconds */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 70
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffffff',
              borderRadius: 1,
              minWidth: 60,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #ecf0f1'
            }}
          >
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                lineHeight: 1
              }}
            >
              {formatNumber(timeLeft.seconds)}
            </Typography>
          </Box>
          <Typography
            variant='caption'
            sx={{
              color: '#7f8c8d',
              fontWeight: 600,
              mt: 1,
              textTransform: 'uppercase',
              fontSize: '0.7rem'
            }}
          >
            Seconds
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default DeploymentCountdown
